/** @forge-header
 * @id          placeOrderModal
 * @role        Order placement modal | cost preview, expiry selection, contract/USD input toggle
 * @pattern     component
 * @tier        T2
 * @purity      none
 * @units       cents | dollars
 * @money-path  yes
 * @fan-out     ~1
 * @upstream    orderMath:computeCost,contractsFromBudget,OrderCost
 *              | nbaConsolidated/format:fmtProbFromCents
 *              | nbaConsolidated/kalshiNbaTicker:parseKalshiNbaMarketTicker,parseKalshiNbaEventTicker
 *              | formatters:formatStartTimePt
 * @downstream  ValueDashboardView
 * @exports     PlaceOrderModal:component | PlaceOrderRequest:interface
 *              | PlaceOrderSubmission:interface | ExpiryMode:type
 * @test        untested
 * @invariant   cost-source | all cost/fee math delegates to orderMath.computeCost — never inline
 * @invariant   expiry-tz | laWallClockToUtcSeconds converts PT wall-clock to UTC epoch seconds
 * @danger      laWallClockToUtcSeconds is best-effort DST — iterative Intl.DateTimeFormat guess
 *              | related to B-001 (+1h timezone bug)
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/atoms/Modal';
import { Money } from '@/components/atoms/Money';
import { NumericStepper } from '@/components/molecules/NumericStepper';
import { cn } from '@/lib/infra/utils';
import { fmtProbFromCents } from '@/lib/nbaConsolidated/format';
import { formatStartTimePt } from '@/lib/infra/formatters';
import {
  parseKalshiNbaMarketTicker,
  parseKalshiNbaEventTicker,
} from '@/lib/nbaConsolidated/kalshiNbaTicker';
import { computeCost, contractsFromBudget } from '@/lib/trading/orderMath';
import type { OrderCost } from '@/lib/trading/orderMath';
import type { Sport } from '@/lib/sportsDiscovery';
import { getSportBufferMinutes } from '@/lib/nbaConsolidated/startTimeSettings';

export type ExpiryMode = 'gtc' | 'preset' | 'custom';

export interface PlaceOrderRequest {
  venue: 'kalshi' | 'polymarket';
  sport?: Sport;
  marketTicker: string;
  tokenId?: string;
  conditionId?: string;
  tickSize?: string;
  negRisk?: boolean;
  side: 'yes' | 'no';
  priceCents: number;
  isTakerMode: boolean;
  teamLabel: string;
  gameDate: string; // YYYY-MM-DD (best-effort; required for Kalshi expiry convenience)
  startTimePt?: string | null; // HH:MM
  /** Earliest Kalshi-reported start time across current slate (HH:MM 24h PT) */
  slateMinTime?: string | null;
  /** Latest Kalshi-reported start time across current slate (HH:MM 24h PT) */
  slateMaxTime?: string | null;
  defaultContracts?: number;
  /** Present when trade originated from an order book sweep click */
  sweepContext?: { levelCount: number; totalDollars: number; side: 'bid' | 'ask' };
}

export interface PlaceOrderSubmission {
  contracts: number;
  /** Limit price in cents (may differ from request.priceCents if user edited it) */
  priceCents: number;
  expiryMode: ExpiryMode;
  /** UTC epoch seconds (optional) */
  expirationTs?: number;
}

type InputMode = 'contracts' | 'usd';

function parseYmd(date: string): { y: number; m: number; d: number } | null {
  const m = String(date || '')
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return { y, m: mo, d };
}

function parseHhMm(hhmm: string): { hh: number; mm: number } | null {
  const m = String(hhmm || '')
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return { hh, mm };
}

/**
 * Convert a wall-clock time in America/Los_Angeles to UTC epoch seconds.
 * Best-effort without Temporal; iterates to handle DST offsets.
 */
function laWallClockToUtcSeconds(args: { dateYmd: string; timeHhMm: string }): number | null {
  const d = parseYmd(args.dateYmd);
  const t = parseHhMm(args.timeHhMm);
  if (!d || !t) return null;

  const desired = { year: d.y, month: d.m, day: d.d, hour: t.hh, minute: t.mm };

  // Start from a UTC guess with same components.
  let guessMs = Date.UTC(
    desired.year,
    desired.month - 1,
    desired.day,
    desired.hour,
    desired.minute,
    0
  );

  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  for (let i = 0; i < 3; i++) {
    const parts = fmt.formatToParts(new Date(guessMs));
    const get = (type: string) => parts.find((p) => p.type === type)?.value;
    const y = Number(get('year'));
    const mo = Number(get('month'));
    const da = Number(get('day'));
    const hh = Number(get('hour'));
    const mm = Number(get('minute'));
    if (![y, mo, da, hh, mm].every((n) => Number.isFinite(n))) return null;

    const deltaMinutes =
      (desired.year - y) * 525600 +
      (desired.month - mo) * 43200 +
      (desired.day - da) * 1440 +
      (desired.hour - hh) * 60 +
      (desired.minute - mm);

    if (deltaMinutes === 0) break;
    guessMs += deltaMinutes * 60_000;
  }

  return Math.floor(guessMs / 1000);
}

/**
 * Format YYYY-MM-DD to a readable date string like "Jan 28, 2026"
 */
function formatGameDate(dateStr: string): string {
  const parsed = parseYmd(dateStr);
  if (!parsed) return dateStr;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[parsed.m - 1]} ${parsed.d}, ${parsed.y}`;
}

/** Fallback range when no slate data available */
const FALLBACK_MIN = '16:00';
const FALLBACK_MAX = '21:00';

interface PresetTime {
  /** 24h HH:MM in PT */
  value: string;
  /** Display label like "4:10 PM PT" */
  label: string;
}

/** Convert HH:MM to total minutes */
function toMinutes(hhmm: string): number | null {
  const t = parseHhMm(hhmm);
  return t ? t.hh * 60 + t.mm : null;
}

/** Convert total minutes to HH:MM */
function fromMinutes(totalMin: number): string {
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Generate preset expiry times from the slate's [min, max] Kalshi times.
 * Range = [slateMin, slateMax] snapped to half-hour floor/ceil, + sport buffer.
 * Falls back to 4:00–9:00 PM PT when no slate data.
 */
function generatePresetTimes(
  sport?: Sport | null,
  slateMinTime?: string | null,
  slateMaxTime?: string | null
): PresetTime[] {
  const buffer = getSportBufferMinutes(sport);

  // Determine range from slate or fallback
  const rawMin = toMinutes(slateMinTime ?? FALLBACK_MIN) ?? toMinutes(FALLBACK_MIN)!;
  const rawMax = toMinutes(slateMaxTime ?? FALLBACK_MAX) ?? toMinutes(FALLBACK_MAX)!;

  // Snap to half-hour: floor the min, ceil the max
  const rangeMin = Math.floor(rawMin / 30) * 30;
  const rangeMax = Math.ceil(rawMax / 30) * 30;

  const presets: PresetTime[] = [];
  for (let m = rangeMin; m <= rangeMax; m += 30) {
    const buffered = m + buffer;
    const value = fromMinutes(buffered);
    presets.push({ value, label: formatStartTimePt(value) + ' PT' });
  }

  return presets.length > 0
    ? presets
    : [
        {
          value: fromMinutes(19 * 60 + buffer),
          label: formatStartTimePt(fromMinutes(19 * 60 + buffer)) + ' PT',
        },
      ];
}

/** Find the preset closest to a detected start time (HH:MM 24h). */
function findClosestPreset(presets: PresetTime[], startTimePt: string | null | undefined): string {
  if (!startTimePt || presets.length === 0) return presets[0]?.value ?? '19:00';
  const t = parseHhMm(startTimePt);
  if (!t) return presets[0].value;
  const targetMin = t.hh * 60 + t.mm;
  let best = presets[0];
  let bestDist = Infinity;
  for (const p of presets) {
    const pt = parseHhMm(p.value);
    if (!pt) continue;
    const dist = Math.abs(pt.hh * 60 + pt.mm - targetMin);
    if (dist < bestDist) {
      bestDist = dist;
      best = p;
    }
  }
  return best.value;
}

/**
 * Parse game context from the market ticker for display
 */
function OrderContextHeader({ request }: { request: PlaceOrderRequest }) {
  // Try to parse Kalshi ticker for rich display
  const marketInfo = parseKalshiNbaMarketTicker(request.marketTicker);
  const eventInfo = marketInfo ? parseKalshiNbaEventTicker(marketInfo.eventTicker) : null;

  // Format the matchup if we have event info
  const matchup = eventInfo ? `${eventInfo.awayCode} @ ${eventInfo.homeCode}` : null;
  const formattedDate = formatGameDate(request.gameDate);

  return (
    <div className="space-y-1">
      {/* Line 1: Team + all badges */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-base font-semibold">{request.teamLabel}</span>
        <span className="rounded bg-green-500/20 px-1.5 py-0.5 text-xs font-bold uppercase text-green-400">
          Win
        </span>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-xs font-medium uppercase',
            request.venue === 'kalshi'
              ? 'bg-blue-500/20 text-blue-400'
              : 'bg-purple-500/20 text-purple-400'
          )}
        >
          {request.venue}
        </span>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-xs font-medium uppercase',
            request.isTakerMode
              ? 'bg-orange-500/20 text-orange-400'
              : 'bg-cyan-500/20 text-cyan-400'
          )}
        >
          {request.isTakerMode ? 'Taker' : 'Maker'}
        </span>
      </div>

      {/* Line 2: Matchup, date, and ticker */}
      <div className="text-muted-foreground font-mono text-xs">
        {matchup && <span>{matchup}</span>}
        {matchup && <span className="mx-1.5">·</span>}
        <span>{formattedDate}</span>
        <span className="mx-1.5">·</span>
        <span
          className="opacity-50"
          title={request.marketTicker}
        >
          {request.marketTicker}
        </span>
      </div>
    </div>
  );
}

/**
 * Cost/Payout summary card — shows the order economics at a glance.
 */
function OrderCostCard({ cost }: { cost: OrderCost }) {
  return (
    <div className="border-border bg-muted/30 rounded-lg border p-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">Total Cost</span>
        <Money
          value={cost.costDollars}
          className="font-mono font-medium"
        />
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-muted-foreground">Max Payout</span>
        <Money
          value={cost.maxWinDollars}
          className="font-mono font-medium"
        />
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-muted-foreground">Max Profit</span>
        <Money
          value={cost.maxProfitDollars}
          variant="pnl"
          className="font-mono font-semibold"
        />
      </div>
      {cost.feeDollars > 0 && (
        <div className="mt-1 flex items-center justify-between">
          <span className="text-muted-foreground">Fee (7%)</span>
          <Money
            value={cost.feeDollars}
            className="text-muted-foreground font-mono"
          />
        </div>
      )}
    </div>
  );
}

export function PlaceOrderModal(props: {
  isOpen: boolean;
  request: PlaceOrderRequest | null;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (submission: PlaceOrderSubmission) => void;
}) {
  const { isOpen, request, submitting = false, error, onClose, onSubmit } = props;

  const [contracts, setContracts] = useState<number>(request?.defaultContracts ?? 100);
  const [priceCents, setPriceCents] = useState<number>(request?.priceCents ?? 50);
  const [inputMode, setInputMode] = useState<InputMode>('contracts');
  const [usdInput, setUsdInput] = useState<string>('');
  const [expiryMode, setExpiryMode] = useState<ExpiryMode>('preset');
  const [selectedPreset, setSelectedPreset] = useState<string>('19:00');
  const [expiryDate, setExpiryDate] = useState<string>(request?.gameDate ?? '');
  const [customExpiry, setCustomExpiry] = useState<string>(''); // YYYY-MM-DD HH:MM (local time)

  // Generate sport-specific preset times from slate range
  const presetTimes = useMemo(
    () => generatePresetTimes(request?.sport, request?.slateMinTime, request?.slateMaxTime),
    [request?.sport, request?.slateMinTime, request?.slateMaxTime]
  );

  const priceEdited = request ? priceCents !== request.priceCents : false;

  // Compute order cost whenever contracts/price/side changes
  const orderCost = useMemo<OrderCost | null>(() => {
    if (!request || contracts <= 0 || priceCents <= 0) return null;
    return computeCost(priceCents, contracts, request.side, request.venue, request.isTakerMode);
  }, [request, contracts, priceCents]);

  // Handle USD input → auto-compute contracts
  const handleUsdChange = (value: string) => {
    setUsdInput(value);
    if (!request) return;
    const dollars = parseFloat(value.replace(/[$,]/g, ''));
    if (!Number.isFinite(dollars) || dollars <= 0) return;
    const computed = contractsFromBudget(dollars, priceCents, request.side);
    if (computed > 0) setContracts(computed);
  };

  // Handle contracts change → update USD display
  const handleContractsChange = (value: number) => {
    setContracts(value);
    if (request) {
      const cost = computeCost(priceCents, value, request.side, request.venue, request.isTakerMode);
      setUsdInput(cost.costDollars.toFixed(2));
    }
  };

  const computedSubmission = useMemo<PlaceOrderSubmission | null>(() => {
    if (!request) return null;
    if (request.venue === 'polymarket') return { contracts, priceCents, expiryMode: 'gtc' };
    if (expiryMode === 'gtc') return { contracts, priceCents, expiryMode };
    if (expiryMode === 'preset') {
      if (!expiryDate) return { contracts, priceCents, expiryMode };
      const ts = laWallClockToUtcSeconds({
        dateYmd: expiryDate,
        timeHhMm: selectedPreset,
      });
      return ts
        ? { contracts, priceCents, expiryMode, expirationTs: ts }
        : { contracts, priceCents, expiryMode };
    }
    // custom
    if (!customExpiry.trim()) return { contracts, priceCents, expiryMode };
    const m = customExpiry.trim().match(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2})$/);
    if (!m) return { contracts, priceCents, expiryMode };
    const ts = laWallClockToUtcSeconds({ dateYmd: m[1], timeHhMm: m[2] });
    return ts
      ? { contracts, priceCents, expiryMode, expirationTs: ts }
      : { contracts, priceCents, expiryMode };
  }, [contracts, priceCents, customExpiry, expiryDate, expiryMode, selectedPreset, request]);

  // Expiry-in-the-past guard (re-evaluated on each render)
  const isExpiryPast =
    computedSubmission?.expirationTs != null &&
    computedSubmission.expirationTs < Math.floor(Date.now() / 1000) + 60;

  // Reset when request changes (new cell clicked)
  useEffect(() => {
    const defaultContracts = request?.defaultContracts ?? 100;
    setContracts(defaultContracts);
    setPriceCents(request?.priceCents ?? 50);
    setInputMode('contracts');
    setUsdInput('');
    setExpiryDate(request?.gameDate ?? '');
    if (request?.venue === 'kalshi') {
      // Auto-select preset closest to detected game start time
      const presets = generatePresetTimes(
        request?.sport,
        request?.slateMinTime,
        request?.slateMaxTime
      );
      const closest = findClosestPreset(presets, request?.startTimePt);
      // If the best preset is already in the past, default to GTC instead of
      // silently disabling the Place Order button (S43b fix)
      const ts = laWallClockToUtcSeconds({
        dateYmd: request?.gameDate ?? '',
        timeHhMm: closest,
      });
      const nowSec = Math.floor(Date.now() / 1000);
      if (ts != null && ts < nowSec + 60) {
        setExpiryMode('gtc');
      } else {
        setExpiryMode('preset');
        setSelectedPreset(closest);
      }
    } else {
      setExpiryMode('gtc');
    }
    setCustomExpiry('');
  }, [
    request?.marketTicker,
    request?.priceCents,
    request?.isTakerMode,
    request?.startTimePt,
    request?.venue,
    request?.sport,
    request?.slateMinTime,
    request?.slateMaxTime,
  ]);

  if (!isOpen || !request) return null;

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <button
        onClick={onClose}
        disabled={submitting}
        className="text-muted-foreground hover:text-foreground px-4 py-2 text-sm transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        onClick={() => {
          if (!computedSubmission) return;
          onSubmit(computedSubmission);
        }}
        disabled={submitting || isExpiryPast}
        className={cn(
          'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          (submitting || isExpiryPast) && 'cursor-not-allowed opacity-50'
        )}
      >
        {submitting ? 'Submitting…' : 'Place Order'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="PLACE ORDER"
      size="md"
      footer={footer}
    >
      <div className="space-y-4">
        {/* Order context header */}
        <OrderContextHeader request={request} />

        {/* Order error — top of modal so it's impossible to miss */}
        {error && (
          <div className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-400">
            {error}
          </div>
        )}

        {/* Sweep context badge */}
        {request.sweepContext && (
          <div className="flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-400">
            <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase">
              Sweep
            </span>
            <span>
              {request.sweepContext.levelCount} level
              {request.sweepContext.levelCount !== 1 ? 's' : ''}
              {' · '}$
              {request.sweepContext.totalDollars >= 1000
                ? `${(request.sweepContext.totalDollars / 1000).toFixed(1)}K`
                : request.sweepContext.totalDollars.toFixed(2)}{' '}
              notional
            </span>
          </div>
        )}

        <div className="border-border bg-muted/30 rounded-lg border p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Side</span>
            <span className="font-mono font-medium uppercase">
              {request.side}
              {request.teamLabel && (
                <span className="text-muted-foreground ml-1.5 text-xs normal-case">
                  ({request.teamLabel} Win)
                </span>
              )}
            </span>
          </div>
          <div className="mt-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-muted-foreground text-xs font-medium">Limit Price</span>
              {priceEdited && (
                <button
                  type="button"
                  onClick={() => setPriceCents(request.priceCents)}
                  className="text-primary hover:text-primary/80 text-xs transition-colors"
                >
                  Reset to {fmtProbFromCents(request.priceCents)}
                </button>
              )}
            </div>
            <NumericStepper
              value={priceCents}
              onChange={setPriceCents}
              min={1}
              max={99}
              step={1}
              suffix="¢"
              size="sm"
              disabled={submitting}
            />
          </div>
        </div>

        {/* Input mode toggle: USD vs Contracts */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-muted-foreground text-xs font-medium">
              {inputMode === 'usd' ? 'Amount (USD)' : 'Contracts'}
            </label>
            <button
              type="button"
              onClick={() => {
                const next = inputMode === 'contracts' ? 'usd' : 'contracts';
                setInputMode(next);
                if (next === 'usd' && orderCost) {
                  setUsdInput(orderCost.costDollars.toFixed(2));
                }
              }}
              className="text-primary hover:text-primary/80 text-xs font-medium transition-colors"
            >
              Switch to {inputMode === 'contracts' ? 'USD' : 'Contracts'}
            </button>
          </div>

          {inputMode === 'contracts' ? (
            <NumericStepper
              value={contracts}
              onChange={handleContractsChange}
              min={1}
              step={100}
            />
          ) : (
            <div className="bg-muted border-border flex h-10 items-center overflow-hidden rounded-lg border">
              <span className="text-muted-foreground border-border flex h-full items-center border-r px-3 text-sm">
                $
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={usdInput}
                onChange={(e) => handleUsdChange(e.target.value)}
                placeholder="0.00"
                className="h-full flex-1 bg-transparent px-3 text-center font-mono text-base font-medium outline-none"
                disabled={submitting}
              />
            </div>
          )}

          {/* Secondary display: shows the alternate metric */}
          <div className="text-muted-foreground text-center text-xs">
            {inputMode === 'usd' ? (
              <span>{contracts.toLocaleString()} contracts</span>
            ) : orderCost ? (
              <span>
                <Money
                  value={orderCost.costDollars}
                  className="text-xs"
                />{' '}
                total cost
              </span>
            ) : null}
          </div>
        </div>

        {/* Cost/Payout summary */}
        {orderCost && <OrderCostCard cost={orderCost} />}

        {request.venue === 'kalshi' && (
          <div className="space-y-2">
            <label className="text-muted-foreground text-xs font-medium">Expiry</label>
            <div className="flex gap-2">
              {/* Mode toggle buttons */}
              {(['preset', 'custom', 'gtc'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setExpiryMode(mode)}
                  disabled={submitting}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    expiryMode === mode
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  )}
                >
                  {mode === 'preset' ? 'Preset' : mode === 'custom' ? 'Custom' : 'GTC'}
                </button>
              ))}
            </div>

            {expiryMode === 'preset' && (
              <div className="space-y-1">
                <div className="flex gap-2">
                  {/* Date input (editable) */}
                  <input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    disabled={submitting}
                    className="border-border bg-background h-10 rounded-lg border px-2 font-mono text-sm"
                  />
                  {/* Time preset dropdown */}
                  <select
                    value={selectedPreset}
                    onChange={(e) => setSelectedPreset(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        const idx = presetTimes.findIndex((p) => p.value === selectedPreset);
                        const next =
                          e.key === 'ArrowUp'
                            ? Math.max(0, idx - 1)
                            : Math.min(presetTimes.length - 1, idx + 1);
                        setSelectedPreset(presetTimes[next].value);
                      }
                    }}
                    disabled={submitting}
                    className="border-border bg-background h-10 flex-1 rounded-lg border px-3 font-mono text-sm"
                  >
                    {presetTimes.map((p) => (
                      <option
                        key={p.value}
                        value={p.value}
                      >
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-muted-foreground text-[10px]">
                  {request.startTimePt && (
                    <span>Detected: {formatStartTimePt(request.startTimePt)} PT</span>
                  )}
                  {request.sport && (
                    <span>
                      {request.startTimePt ? ' · ' : ''}Buffer: +
                      {getSportBufferMinutes(request.sport)}m ({request.sport.toUpperCase()})
                    </span>
                  )}
                </div>
              </div>
            )}

            {isExpiryPast && (
              <div className="rounded-md bg-yellow-500/10 px-3 py-1.5 text-xs font-medium text-yellow-400">
                Expiry is in the past — order will be rejected
              </div>
            )}

            {expiryMode === 'custom' && (
              <input
                value={customExpiry}
                onChange={(e) => setCustomExpiry(e.target.value)}
                placeholder="YYYY-MM-DD HH:MM (PT)"
                className="border-border bg-background h-10 w-full rounded-lg border px-3 font-mono text-sm"
                disabled={submitting}
              />
            )}
          </div>
        )}

        {request.venue === 'polymarket' && (
          <div className="border-border bg-muted/30 text-muted-foreground rounded-lg border p-3 text-xs">
            <div>
              Token: <span className="text-foreground/90 font-mono">{request.tokenId ?? '—'}</span>
            </div>
            <div className="mt-1">
              tickSize:{' '}
              <span className="text-foreground/90 font-mono">{request.tickSize ?? '—'}</span> ·
              negRisk:{' '}
              <span className="text-foreground/90 font-mono">
                {String(Boolean(request.negRisk))}
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** @forge-footer
 * @checklist
 *   1. COST MATH | always via computeCost — never inline fee calculation
 *   2. EXPIRY TZ | laWallClockToUtcSeconds iterates to handle DST — test around spring/fall transitions
 *   3. RESET | state resets on request change (marketTicker, priceCents, isTakerMode, venue)
 * @sync-group  fee-model: orderMath.ts + pricing.ts + arbitrage.ts (P*(1-P) must match)
 * @backlog     B-001: +1h timezone offset
 */
